import React from 'react';
import { motion } from 'motion/react';
import { RotateCcw, ArrowLeft, Mail, Phone, MapPin, XCircle, AlertTriangle, ShieldCheck } from 'lucide-react';

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 220, damping: 22 } },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <motion.section variants={sectionVariants} className="mb-8">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)' }}>
          <Icon size={15} className="text-emerald-400" />
        </div>
        <h2 className="text-base font-black text-white tracking-tight">{title}</h2>
      </div>
      <div className="text-[13px] text-white/55 leading-relaxed pl-[42px]">
        {children}
      </div>
    </motion.section>
  );
}

export default function ReturnPolicy() {
  return (
    <div
      className="min-h-screen w-full font-sans flex flex-col"
      style={{ background: 'radial-gradient(ellipse at 60% 0%, #0a1f1a 0%, #040d09 50%, #020807 100%)' }}
    >
      {/* Ambient orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #059669 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute top-1/3 -left-48 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #059669 0%, transparent 60%)', filter: 'blur(80px)' }} />
      </div>

      {/* Sticky Header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-white/5 px-4 py-3 flex items-center shrink-0" style={{ background: 'rgba(4, 13, 9, 0.85)' }}>
        <motion.button
          whileTap={{ scale: 0.94 }}
          onClick={() => window.history.back()}
          className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-xs font-bold"
        >
          <ArrowLeft size={15} />
          Back to Home
        </motion.button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-3xl mx-auto px-5 sm:px-6 pt-8 pb-8"
        >
          {/* Page Header */}
          <motion.div variants={sectionVariants} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl blur-md" style={{ background: 'rgba(16,185,129,0.35)' }} />
                <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: '#064e3b', border: '1px solid #05966980' }}>
                  <RotateCcw size={22} className="text-emerald-400" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Return & Refund Policy</h1>
                <p className="text-xs text-white/40 mt-0.5">Kangavena Cloud · Sri Lanka</p>
              </div>
            </div>
          </motion.div>

          {/* How Our Billing Works */}
          <Section icon={ShieldCheck} title="Billing & Payment">
            <p className="mb-3">
              Kangavena Cloud operates on a <strong className="text-white/70">revenue-share model with no upfront subscription fees</strong>:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Revenue Share:</strong> Kangavena charges between <strong className="text-white/70">1% to 2.5%</strong> of your store's monthly net profit generated through the POS system. The exact percentage is agreed upon during onboarding.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Monthly Billing Cycle:</strong> At the end of each calendar month, Kangavena will calculate your net profit and send you a notice with the available balance due. Payment is expected within the following month.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span><strong className="text-white/70">Late Payment & System Lock:</strong> If payment is not received by the end of the second month (60 days from the original billing notice), Kangavena reserves the right to lock down your POS system, restricting access to all features until the outstanding balance is settled in full.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>All fees are calculated and quoted in <strong className="text-white/70">Sri Lankan Rupees (LKR)</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>We reserve the right to adjust the revenue-share percentage with <strong className="text-white/70">30 days' advance written notice</strong>.</span>
              </li>
            </ul>
          </Section>

          {/* Billing Disputes */}
          <Section icon={RotateCcw} title="Billing Disputes & Adjustments">
            <p className="mb-3">
              We are committed to fair and transparent billing. If you believe your monthly revenue-share calculation is incorrect:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>You may <strong className="text-white/70">request a billing review</strong> within <strong className="text-white/70">14 days</strong> of receiving your monthly billing notice.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Kangavena provides transparent access to your store's sales data so you can verify the net profit calculation independently.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>If an overcharge is confirmed, the excess amount will be <strong className="text-white/70">credited to your next billing cycle</strong>.</span>
              </li>
            </ul>
          </Section>

          {/* Non-Refundable Charges */}
          <Section icon={XCircle} title="Non-Refundable Charges">
            <p className="mb-3">The following are not eligible for credits or adjustments:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Revenue-share amounts that have already been paid and are beyond the 14-day dispute window.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Custom development work, including custom integrations, configurations, or modifications requested by the user.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Training or consulting services that have already been delivered.</span>
              </li>
            </ul>
          </Section>

          {/* Cancellation */}
          <Section icon={XCircle} title="Cancellation">
            <p className="mb-3">
              You may stop using the Service at any time. Since there are no upfront subscription fees, cancellation simply means you stop using the POS system. However:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>Any outstanding revenue-share balance for the current billing period must be settled before your account is fully closed.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <span>After cancellation, your account and data will be retained for 30 days for data export, then permanently deleted.</span>
              </li>
            </ul>
          </Section>

          {/* Hardware Returns */}
          <Section icon={AlertTriangle} title="Hardware Returns (If Applicable)">
            <p>
              If you have purchased any hardware (tablets, receipt printers, barcode scanners, or other POS equipment) directly through Kangavena, defective hardware may be returned within <strong className="text-white/70">30 days of purchase</strong>. The hardware must be in its original condition, with all accessories and packaging. Contact us to initiate a return — shipping costs for defective items will be covered by Kangavena.
            </p>
          </Section>

          {/* Contact */}
          <Section icon={Mail} title="Contact Us">
            <p className="mb-3">
              For any billing disputes, cancellation requests, or hardware returns:
            </p>
            <div className="rounded-2xl p-4 mt-2" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
              <div className="space-y-2.5">
                <a href="mailto:sissara.com@gmail.com" className="flex items-center gap-2.5 text-white/60 hover:text-emerald-400 transition-colors">
                  <Mail size={14} className="text-emerald-500" />
                  <span className="text-sm">sissara.com@gmail.com</span>
                </a>
                <a href="https://wa.me/94767114888" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-white/60 hover:text-emerald-400 transition-colors">
                  <Phone size={14} className="text-emerald-500" />
                  <span className="text-sm">076 711 4888 (WhatsApp)</span>
                </a>
                <div className="flex items-center gap-2.5 text-white/60">
                  <MapPin size={14} className="text-emerald-500" />
                  <span className="text-sm">Sri Lanka</span>
                </div>
              </div>
            </div>
          </Section>

          {/* Back to Home */}
          <motion.div variants={sectionVariants} className="text-center pt-4 pb-8">
            <a href="/" className="inline-flex items-center gap-2 text-xs font-bold text-emerald-400/60 hover:text-emerald-400 transition-colors">
              <ArrowLeft size={13} />
              Back to Home
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
